import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Filter,
  Globe2,
  Megaphone,
  FileText,
  Users,
  Send,
  Share2,
  Radio,
  AlertTriangle,
} from 'lucide-react';
import { useRedirect } from '../../../Shared/Hooks/useRedirect ';
import { getLimitAlertSummary } from '../../Services/GrowthService';
import { LimitAlertSummary } from '../../Types/GrowthType';

const TABS = [
  { label: 'Overview', path: '/admin/growth', icon: LayoutGrid },
  { label: 'Funnel', path: '/admin/growth/funnel', icon: Filter },
  { label: 'Sources', path: '/admin/growth/sources', icon: Globe2 },
  { label: 'Campaigns', path: '/admin/growth/campaigns', icon: Megaphone },
  { label: 'Content', path: '/admin/growth/content', icon: FileText },
  { label: 'Prospects', path: '/admin/growth/prospects', icon: Users },
  { label: 'Invitations', path: '/admin/growth/invitations', icon: Send },
  { label: 'Referrals', path: '/admin/growth/referrals', icon: Share2 },
  { label: 'Events', path: '/admin/growth/events', icon: Radio },
  { label: 'Limits Exceeded', path: '/admin/growth/limits', icon: AlertTriangle },
];

const GrowthTabNav = () => {
  const location = useLocation();
  const redirect = useRedirect();
  // Read-only summary, polled rather than pushed: the alert itself arrives through the notification
  // service, and this counter only needs to be roughly current when an admin is navigating.
  const [summary, setSummary] = useState<LimitAlertSummary | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const response = await getLimitAlertSummary();
        if (active && response.success && response.data) {
          setSummary(response.data);
        }
      } catch {
        // A failed badge must not take the nav down with it.
      }
    };

    load();
    const timer = setInterval(load, 120000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [location.pathname]);

  return (
    <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
      {TABS.map((tab) => {
        const active =
          location.pathname === tab.path ||
          (tab.path !== '/admin/growth' && location.pathname.startsWith(tab.path));
        const Icon = tab.icon;
        const badge = tab.path === '/admin/growth/limits' ? (summary?.totalOpen ?? 0) : 0;
        return (
          <button
            key={tab.path}
            onClick={() => redirect(tab.path)}
            className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
              active
                ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
            }`}
          >
            <Icon size={14} />
            {tab.label}
            {badge > 0 && (
              <span
                className={`rounded-md px-1.5 py-0.5 text-[10px] font-black ${
                  active ? 'bg-slate-950/20 text-slate-950' : 'bg-rose-500/20 text-rose-400'
                }`}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default GrowthTabNav;